// client/src/components/Sidebar.js
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Typography,
    Avatar,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Fade,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Drawer,
    useTheme,
    useMediaQuery,
    Button
} from '@mui/material';
import {
    Users,
    Clock,
    CreditCard,
    LogOut,
    Shield,
    ClipboardList,
    UserPlus,
    ShieldAlert,
    UserCheck,
    ChevronLeft,
    ChevronRight,
    BookOpen,
    Settings,
    Cloud
} from 'lucide-react';

const Sidebar = ({ mobileOpen, setMobileOpen }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const navigate = useNavigate();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

    const name = localStorage.getItem('user_name') || 'Workspace User';
    const rawRole = localStorage.getItem('user_role') || 'BDE';
    const roleLower = String(rawRole).trim().toLowerCase();

    const isBDE = roleLower === 'bde' || roleLower === 'business development executive';
    const isAdmin = roleLower === 'admin';
    const isHR = roleLower === 'hr';

    const prefix = isAdmin ? '/admin' : isHR ? '/hr' : '/bde';

    const handleConfirmLogout = () => {
        setLogoutDialogOpen(false);
        localStorage.clear();
        navigate('/login');
    };

    const handleNavigation = (path) => {
        navigate(path);
        if (isMobile && setMobileOpen) {
            setMobileOpen(false);
        }
    };

    const getNavItems = () => {
        let items = [
            { text: 'Leads Matrix', path: `${prefix}/leads`, icon: <Users size={18} /> },
            { text: 'My Profile', path: `${prefix}/profile`, icon: <UserCheck size={18} /> }
        ];

        if (isBDE || isAdmin || isHR) {
            items.push({ text: 'My Attendance', path: `${prefix}/attendance`, icon: <Clock size={18} /> });
        }

        if (isBDE) {
            items.push({ text: 'My Leaves', path: '/bde/my-leaves', icon: <ClipboardList size={18} /> });
        } else {
            // items.push({ text: 'Leave Management', path: `${prefix}/leaves`, icon: <ClipboardList size={18} /> });
            items.push({ text: 'Approval Console', path: `${prefix}/approvals`, icon: <ShieldAlert size={18} /> });
        }

        items.push({ text: 'Sales Toolkit', path: `${prefix}/toolkit`, icon: <BookOpen size={18} /> });

        // 🛡️ Safe Hierarchy Guards
        if (isHR || isAdmin) {
            const attPath = isAdmin ? '/admin/hr-attendance' : '/hr/hr-attendance';
            items.push({ text: 'Attendance Logs', path: attPath, icon: <ClipboardList size={18} /> });
            items.push({ text: 'Employee Central', path: `${prefix}/employees`, icon: <Users size={18} /> });
            items.push({ text: 'Manage Employees', path: `${prefix}/manage-employees`, icon: <Settings size={18} /> });
            items.push({ text: 'Add New Profile', path: `${prefix}/add-employee`, icon: <UserPlus size={18} /> });
        }

        if (isAdmin || isBDE) {
            items.push({ text: 'Revenue Ledger', path: `${prefix}/payments`, icon: <CreditCard size={18} /> });
        }

        return items;
    };

    // --- REUSABLE INNER CONTENT (SALESFORCE LIGHTNING DESIGN THEME) ---

    // ⚡ PRE-COMPUTE SETUP ACTIVE PATH STATE MATCHING
    const settingsPath = `${prefix}/settings`;
    const isSettingsActive = location.pathname === settingsPath || location.pathname.startsWith(settingsPath + '/');

    const sidebarContent = (
        <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            boxSizing: 'border-box'
        }}>
            {/* Top Workspace Area */}
            <Box sx={{ overflowY: 'auto', overflowX: 'hidden', flexGrow: 1, '&::-webkit-scrollbar': { width: '0px' } }}>

                {/* Salesforce App Launcher style Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, height: 56, px: 2, bgcolor: '#0176d3', color: '#ffffff' }}>
                    <Box sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        width: 32, height: 32, borderRadius: '4px',
                        bgcolor: '#ffffff', color: '#0176d3', flexShrink: 0
                    }}>
                        <Cloud size={18} strokeWidth={2.5} />
                    </Box>
                    {(!isCollapsed || isMobile) && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ffffff', fontSize: '0.85rem', letterSpacing: '0.05em', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
                                TECHN GLOBAL
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#e0f2fe', fontWeight: 500, fontSize: '10px', whiteSpace: 'nowrap' }}>
                                Core CRM Console
                            </Typography>
                        </Box>
                    )}
                </Box>

                {/* Profile Info Subheader */}
                <Box sx={{
                    display: 'flex', alignItems: 'center',
                    p: 2, bgcolor: '#f3f3f3',
                    borderBottom: '1px solid #c9c9c9',
                    justifyContent: (isCollapsed && !isMobile) ? 'center' : 'flex-start'
                }}>
                    <Tooltip title={(isCollapsed && !isMobile) ? name : ""} placement="right" arrow>
                        <Avatar sx={{
                            bgcolor: '#0176d3', color: '#ffffff',
                            width: 32, height: 32, fontSize: '12px', fontWeight: 600,
                            borderRadius: '4px'
                        }}>
                            {name ? name.charAt(0).toUpperCase() : 'U'}
                        </Avatar>
                    </Tooltip>
                    {(!isCollapsed || isMobile) && (
                        <Box sx={{ ml: 1.5, overflow: 'hidden', whiteSpace: 'nowrap', flexGrow: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#181818', fontSize: '13px', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                                {name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {isAdmin && <Shield size={10} color="#0176d3" />}
                                <Typography variant="caption" sx={{ color: '#444444', fontWeight: 500, fontSize: '11px', textTransform: 'capitalize' }}>
                                    {roleLower || 'Standard User'}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Navigation Items */}
                <List component="nav" disablePadding sx={{ display: 'flex', flexDirection: 'column' }}>
                    {getNavItems().map((item) => {
                        const isActive = location.pathname === item.path ||
                            (item.path !== prefix && location.pathname.startsWith(item.path + '/'));

                        const listItemContent = (
                            <ListItemButton
                                onClick={() => handleNavigation(item.path)}
                                disableRipple
                                sx={{
                                    height: 40,
                                    px: (isCollapsed && !isMobile) ? 0 : 2,
                                    justifyContent: (isCollapsed && !isMobile) ? 'center' : 'flex-start',
                                    bgcolor: isActive ? '#ffffff' : 'transparent',
                                    color: isActive ? '#0176d3' : '#181818',
                                    position: 'relative',
                                    borderBottom: '1px solid #e5e5e5',
                                    borderRadius: 0,
                                    transition: 'background-color 0.1s ease',
                                    '&::before': {
                                        content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px',
                                        bgcolor: '#0176d3', opacity: isActive ? 1 : 0
                                    },
                                    '&:hover': {
                                        bgcolor: isActive ? '#ffffff' : '#f3f3f3',
                                        color: '#0176d3'
                                    }
                                }}
                            >
                                <ListItemIcon sx={{
                                    minWidth: (isCollapsed && !isMobile) ? 0 : 32, justifyContent: 'center',
                                    color: isActive ? '#0176d3' : '#747474'
                                }}>
                                    {item.icon}
                                </ListItemIcon>
                                {(!isCollapsed || isMobile) && (
                                    <ListItemText primary={item.text} primaryTypographyProps={{ fontSize: '13px', fontWeight: isActive ? 600 : 400, color: 'inherit' }} />
                                )}
                            </ListItemButton>
                        );

                        return (
                            <ListItem key={item.path} disablePadding sx={{ display: 'block' }}>
                                {(isCollapsed && !isMobile) ? (
                                    <Tooltip title={item.text} placement="right" arrow transitionComponent={Fade}>
                                        <Box>{listItemContent}</Box>
                                    </Tooltip>
                                ) : listItemContent}
                            </ListItem>
                        );
                    })}
                </List>
            </Box>

            {/* Bottom Actions Utilities Panel */}
            <Box component="footer" sx={{ display: 'flex', flexDirection: 'column', bgcolor: '#f3f3f3', borderTop: '1px solid #c9c9c9' }}>
                <Tooltip title={(isCollapsed && !isMobile) ? "Setup / Settings" : ""} placement="right" arrow transitionComponent={Fade}>
                    <ListItemButton
                        onClick={() => handleNavigation(settingsPath)}
                        disableRipple
                        sx={{
                            height: 40,
                            px: (isCollapsed && !isMobile) ? 0 : 2,
                            justifyContent: (isCollapsed && !isMobile) ? 'center' : 'flex-start',
                            color: isSettingsActive ? '#0176d3' : '#181818',
                            bgcolor: isSettingsActive ? '#ffffff' : 'transparent',
                            borderRadius: 0,
                            position: 'relative',
                            '&::before': {
                                content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: '4px',
                                bgcolor: '#0176d3', opacity: isSettingsActive ? 1 : 0
                            },
                            '&:hover': { bgcolor: isSettingsActive ? '#ffffff' : '#e5e5e5' }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: (isCollapsed && !isMobile) ? 0 : 32, justifyContent: 'center', color: 'inherit' }}>
                            <Settings size={18} />
                        </ListItemIcon>
                        {(!isCollapsed || isMobile) && <ListItemText primary="Setup" primaryTypographyProps={{ fontSize: '13px', fontWeight: isSettingsActive ? 600 : 400 }} />}
                    </ListItemButton>
                </Tooltip>

                <Tooltip title={(isCollapsed && !isMobile) ? "Logout" : ""} placement="right" arrow transitionComponent={Fade}>
                    <ListItemButton
                        onClick={() => setLogoutDialogOpen(true)}
                        disableRipple
                        sx={{
                            height: 40, px: (isCollapsed && !isMobile) ? 0 : 2, justifyContent: (isCollapsed && !isMobile) ? 'center' : 'flex-start',
                            color: '#ba0517',
                            borderRadius: 0,
                            '&:hover': { bgcolor: '#ffe8ea' }
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: (isCollapsed && !isMobile) ? 0 : 32, justifyContent: 'center', color: 'inherit' }}>
                            <LogOut size={18} />
                        </ListItemIcon>
                        {(!isCollapsed || isMobile) && <ListItemText primary="Log Out" primaryTypographyProps={{ fontSize: '13px', fontWeight: 500 }} />}
                    </ListItemButton>
                </Tooltip>
            </Box>
        </Box>
    );

    const logoutDialogOverlay = (
        <Dialog
            open={logoutDialogOpen}
            onClose={() => setLogoutDialogOpen(false)}
            PaperProps={{ sx: { borderRadius: '4px', p: 0, maxWidth: '400px' } }}
        >
            <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem', bgcolor: '#f3f3f3', borderBottom: '1px solid #c9c9c9', py: 1.5, px: 3 }}>Confirm Log Out</DialogTitle>
            <DialogContent sx={{ pt: 3, px: 3 }}><DialogContentText sx={{ fontSize: '13px', color: '#181818' }}>Are you sure you want to log out of your workspace session?</DialogContentText></DialogContent>
            <DialogActions sx={{ p: 2, bgcolor: '#f3f3f3', borderTop: '1px solid #c9c9c9', gap: 1 }}>
                <Button onClick={() => setLogoutDialogOpen(false)} variant="outlined" sx={{ textTransform: 'none', color: '#0176d3', borderColor: '#0176d3', fontWeight: 500, borderRadius: '4px' }}>Cancel</Button>
                <Button onClick={handleConfirmLogout} variant="contained" disableElevation sx={{ textTransform: 'none', bgcolor: '#ba0517', color: '#ffffff', fontWeight: 500, borderRadius: '4px', '&:hover': { bgcolor: '#8e0311' } }}>Log Out</Button>
            </DialogActions>
        </Dialog>
    );

    if (isMobile) {
        return (
            <>
                <Drawer
                    variant="temporary"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    ModalProps={{ keepMounted: true }}
                    PaperProps={{ sx: { width: 260, border: 'none', bgcolor: '#ffffff' } }}
                >
                    {sidebarContent}
                </Drawer>
                {logoutDialogOverlay}
            </>
        );
    }

    return (
        <Box sx={{
            width: isCollapsed ? 64 : 240, minWidth: isCollapsed ? 64 : 240, maxWidth: isCollapsed ? 64 : 240,
            flexShrink: 0, bgcolor: '#ffffff', color: '#181818', height: '100vh', zIndex: 1200,
            borderRight: '1px solid #c9c9c9',
            transition: 'all 0.15s ease-in-out', position: 'relative'
        }}>
            <IconButton
                onClick={() => setIsCollapsed(!isCollapsed)}
                size="small"
                sx={{
                    position: 'absolute', right: -12, top: 68, bgcolor: '#ffffff', border: '1px solid #c9c9c9',
                    zIndex: 1300, width: 22, height: 22, borderRadius: '50%',
                    '&:hover': { bgcolor: '#f3f3f3' }, transition: 'all 0.1s ease'
                }}
            >
                {isCollapsed ? <ChevronRight size={12} color="#747474" /> : <ChevronLeft size={12} color="#747474" />}
            </IconButton>

            {sidebarContent}
            {logoutDialogOverlay}
        </Box>
    );
};

export default Sidebar;